import { compact, groupBy, orderBy, uniq, uniqBy } from 'lodash';
import { DateTime } from 'luxon';

import type {
  OrganizationMember,
  RegistrarInfo,
  SubUnitAdultInfo,
  SubUnitYouthInfo,
} from '@appTypes/esb';
import { findRegistrationRecord } from '@modules/advancement/packRoster/utilsTyped';
import { PositionId, programNames } from '@shared/constants';

export enum MemberKind {
  Youth = 'youth',
  Adult = 'adult',
  Participant = 'participant',
}

/**
 * Positions that rank first in the roster (unit leadership, then youth member).
 * Ordering is applied at construction time, not in column sorters.
 */
export const PRIORITY_POSITIONS = [
  'Scoutmaster',
  'Venturing Crew Advisor',
  'Skipper',
  'Cubmaster',
  'Committee Chair',
  'Committee Chairman',
  'Chartered Organization Rep.',
  'Youth Member',
] as const;

const PRIORITY_POSITION_INDEX: ReadonlyMap<string, number> = new Map(
  PRIORITY_POSITIONS.map((p, i) => [p, i]),
);

export interface DedupedPosition {
  positionId: number;
  position: string;
  /** A Scoutbook functional role, not a unit position */
  isFunctional?: boolean;
}

/** Advancement slice, filled from the advancement endpoints on demand. */
export interface AdvancementInfo {
  currentHighestRank?: string;
  hasMinFirstClass?: boolean;
}

export type SubUnitAssignment = SubUnitAdultInfo | SubUnitYouthInfo;

/** Sub-unit (den/patrol) slice, filled from the sub-units endpoint on demand. */
export interface SubUnitInfo {
  assignments: SubUnitAssignment[];
}

/** Fields shared across all member kinds. */
interface RosterMemberShared {
  personGuid: string;
  memberId: string;
  /** `userId` of the sub-unit and den chief endpoints */
  sbUserId: number;
  firstName: string;
  lastName: string;
  middleName?: string | null;
  nickName?: string | null;
  nameSuffix?: string | null;
  personFullName: string;
  gender?: string;
  pictureUrl?: string | null;
  grade?: number | null;
  dateOfBirth?: string | null;
  programs: string[];
  positions: DedupedPosition[];
  isLeader: boolean;
  isDenChief: boolean;
  /**
   * Unit Key 3. Not carried by the org roster endpoints, so it defaults to
   * `false` and is meant to be supplied by enrichment (e.g. the unit roster).
   */
  isKey3: boolean;
  // Enrichment slices. NOTE:  they are `undefined` until their hook loads them.
  registration?: RegistrarInfo;
  advancement?: AdvancementInfo;
  subUnit?: SubUnitInfo;
}

export interface YouthMember extends RosterMemberShared {
  kind: MemberKind.Youth;
}
export interface AdultMember extends RosterMemberShared {
  kind: MemberKind.Adult;
}
export interface ParticipantMember extends RosterMemberShared {
  kind: MemberKind.Participant;
}

export type RosterMember = YouthMember | AdultMember | ParticipantMember;

/** Slices that {@link mergeEnrichment} can attach by `personGuid`. */
export type EnrichmentSlice = 'registration' | 'advancement' | 'subUnit';

// TS guard utils
export const isYouth = (m: RosterMember): m is YouthMember =>
  m.kind === MemberKind.Youth;
export const isAdult = (m: RosterMember): m is AdultMember =>
  m.kind === MemberKind.Adult;
export const isParticipant = (m: RosterMember): m is ParticipantMember =>
  m.kind === MemberKind.Participant;

export function sortPositions(positions: DedupedPosition[]): DedupedPosition[] {
  return orderBy(
    positions,
    [
      p => PRIORITY_POSITION_INDEX.get(p.position) ?? Number.POSITIVE_INFINITY,
      p => p.position.toLowerCase(),
    ],
    ['asc', 'asc'],
  );
}

/** Age in whole years derived from date of birth, or null when unknown. */
export function getAge(m: { dateOfBirth?: string | null }): number | null {
  if (!m.dateOfBirth) return null;
  const dob = DateTime.fromISO(m.dateOfBirth);
  if (!dob.isValid) return null;
  return Math.floor(DateTime.now().diff(dob, 'years').years);
}

/** Youth registration age limit per program (mirrors shared `unitsMaxAge`). */
const MAX_YOUTH_AGE_BY_PROGRAM: Record<string, number> = {
  [programNames.BOY_SCOUT]: 18,
  [programNames.VARSITY]: 21,
  [programNames.VENTURING]: 21,
  [programNames.SEA_SCOUT]: 21,
  [programNames.EXPLORING]: 21,
  [programNames.CLUB]: 21,
};

/**
 * The youth age limit for the member's program(s), or `undefined` when the
 * program has no aging-out limit (e.g. Cub Scout). When a member spans several
 * programs, the most permissive limit wins.
 */
export function getMaxYouthAge(m: { programs: string[] }): number | undefined {
  const limits = m.programs
    .map(p => MAX_YOUTH_AGE_BY_PROGRAM[p])
    .filter((a): a is number => a !== undefined);
  return limits.length ? Math.max(...limits) : undefined;
}

// --- Construction ----------------------------------------------------------

/** Raw member where `registrarInfo` may be absent (e.g. after dedup). */
type RawMember = Omit<OrganizationMember, 'registrarInfo'> & {
  registrarInfo?: RegistrarInfo;
};

/**
 * Refines the base kind: a youth at or over their program's youth age limit is
 * a participant (the legacy roster's "P 18+"). Adults pass through unchanged.
 */
function resolveKind(
  baseKind: MemberKind,
  programs: string[],
  dateOfBirth?: string | null,
): MemberKind {
  if (baseKind !== MemberKind.Youth) return baseKind;
  const maxAge = getMaxYouthAge({ programs });
  const age = getAge({ dateOfBirth });
  return maxAge !== undefined && age !== null && age >= maxAge
    ? MemberKind.Participant
    : baseKind;
}

/**
 * Builds a {@link RosterMember} from a de-duplicated API record.
 * Called only by {@link uniqueMembers}.
 *
 * Add fields here for API/domain data
 * Add to {@link RosterMemberView} for anything derived purely for display e.g. UI Tables.
 */
export function create(
  raw: RawMember,
  baseKind: MemberKind,
  programs: string[],
  positions: DedupedPosition[],
): RosterMember {
  const dateOfBirth = raw.registrarInfo?.dateOfBirth;
  const kind = resolveKind(baseKind, programs, dateOfBirth);
  const shared: RosterMemberShared = {
    personGuid: raw.personGuid,
    memberId: raw.memberId,
    sbUserId: raw.sbUserId,
    firstName: raw.firstName,
    lastName: raw.lastName,
    middleName: raw.middleName,
    nickName: raw.nickName,
    nameSuffix: raw.nameSuffix,
    personFullName: raw.personFullName,
    gender: raw.gender,
    pictureUrl: raw.pictureUrl,
    grade: raw.grade,
    dateOfBirth,
    programs,
    positions,
    isLeader: kind === MemberKind.Adult,
    isDenChief: positions.some(p => p.positionId === PositionId.DenChief),
    isKey3: false,
    registration: raw.registrarInfo,
  };
  switch (kind) {
    case MemberKind.Youth:
      return { ...shared, kind };
    case MemberKind.Adult:
      return { ...shared, kind };
    case MemberKind.Participant:
      return { ...shared, kind };
  }
}

type Tagged = OrganizationMember & { _kind: MemberKind };

function pickBestRegistrarInfo(group: Tagged[]): RegistrarInfo | undefined {
  // findRegistrationRecord needs the date fields at the top level, so flatten
  // registrarInfo before calling it, then pull the winning record back.
  const flat = compact(
    group.map(m =>
      m.registrarInfo
        ? {
            personGuid: m.personGuid,
            registrationExpireDt: m.registrarInfo.registrationExpireDt,
            registrationEffectiveDt: m.registrarInfo.registrationEffectiveDt,
            registrarInfo: m.registrarInfo,
          }
        : null,
    ),
  );
  if (flat.length === 0) return undefined;
  return findRegistrationRecord(flat, group[0].personGuid)?.registrarInfo;
}

function collectPositions(group: Tagged[]): DedupedPosition[] {
  const refs = compact(
    group.map(m =>
      m.position
        ? {
            positionId: m.positionId,
            position: m.position,
            isFunctional: m.isFunctional,
          }
        : null,
    ),
  );
  return sortPositions(uniqBy(refs, 'positionId'));
}

/**
 * Merges and dedups the youth and adult lists into a single record keyed by
 * `personGuid`. Youths are spread first, so a person appearing in
 * both lists is typed Youth. Programs/positions are unioned across the person's
 * records; the registration slice is taken from their latest registration.
 */
export function uniqueMembers(
  youths: OrganizationMember[],
  adults: OrganizationMember[],
): RosterMember[] {
  const tagged: Tagged[] = [
    ...youths.map(m => ({ ...m, _kind: MemberKind.Youth })),
    ...adults.map(m => ({ ...m, _kind: MemberKind.Adult })),
  ];
  const grouped = groupBy(tagged, 'personGuid');

  return Object.values(grouped).map(group => {
    const [first] = group;
    return create(
      { ...first, registrarInfo: pickBestRegistrarInfo(group) },
      first._kind,
      uniq(compact(group.map(m => m.program))),
      collectPositions(group),
    );
  });
}

/**
 * Joins an enrichment slice onto members by `personGuid`. Members without a
 * matching entry are returned unchanged.
 */
export function mergeEnrichment(
  members: RosterMember[],
  slice: 'registration',
  byGuid: Record<string, RegistrarInfo>,
): RosterMember[];
export function mergeEnrichment(
  members: RosterMember[],
  slice: 'advancement',
  byGuid: Record<string, AdvancementInfo>,
): RosterMember[];
export function mergeEnrichment(
  members: RosterMember[],
  slice: 'subUnit',
  byGuid: Record<string, SubUnitInfo>,
): RosterMember[];
export function mergeEnrichment(
  members: RosterMember[],
  slice: EnrichmentSlice,
  byGuid: Record<string, RegistrarInfo | AdvancementInfo | SubUnitInfo>,
): RosterMember[] {
  return members.map(m => {
    const data = byGuid[m.personGuid];
    return data ? ({ ...m, [slice]: data } as RosterMember) : m;
  });
}
